import { useEffect } from "react";
import { FieldValues, UseFormGetValues, UseFormTrigger, UseFormWatch } from "react-hook-form";

type UseAutoFormSubmitProps<TFieldValues extends FieldValues> = {
  watch: UseFormWatch<TFieldValues>;
  trigger: UseFormTrigger<TFieldValues>;
  getValues: UseFormGetValues<TFieldValues>;
  onChange: (data: TFieldValues) => void;
};

/**
 * A custom React hook that automatically triggers form submission when a field value changes.
 * This hook is designed to work with React Hook Form (RHF) and ensures that the form is valid
 * before invoking the `onChange` callback with the current form values.
 *
 * @template TFieldValues - The type of the form field values, extending `FieldValues` from RHF.
 *
 * @param {Object} props - The properties required for the hook.
 * @param {Function} props.watch - A function from RHF to subscribe to form field changes.
 * @param {Function} props.trigger - A function from RHF to validate the form.
 * @param {Function} props.getValues - A function from RHF to retrieve the current form values.
 * @param {Function} props.onChange - A callback function invoked when the form is valid and a field value changes.
 *
 * @remarks
 * - The `onChange` callback potentially should be memoized by the caller to avoid unnecessary re-renders.
 */
export function useFormAutoSubmit<TFieldValues extends FieldValues>({
  watch,
  trigger,
  getValues,
  onChange,
}: UseAutoFormSubmitProps<TFieldValues>): void {
  useEffect(() => {
    const subscription = watch(async (currentValues, { name, type }) => {
      // Only proceed if a field value actually changed.
      if (name && type === "change") {
        const isFormValid = await trigger();
        if (isFormValid) {
          onChange(getValues());
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, onChange, trigger, getValues]);
}
